import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Spinner from 'react-bootstrap/Spinner';

import { URLS } from '../../resources/constants';
import { useForecastApi } from '../../resources/hooks/api/useForecastApiHook';
import ForecastLayout from './ForecastLayout';
import ExportPdfMenu from './ExportPdfMenu';
import { money } from './ForecastResults';
import { CASH_ROW_LABELS, MONTH_FULL_NAMES } from './forecastUtils';

export type StatementKind = 'cash_flow' | 'balance_sheet';

interface Series { monthly: number[]; yearly: number[]; total: number }
interface Row {
    key: string;
    label: string;
    series?: Series;
    source?: string;           // IS / BS tag (cash flow)
    cls?: string;              // fyf-section | fyf-total | fyf-sub-row
}

const META: Record<StatementKind, { title: string; subtitle: string; rule: string; tab?: string }> = {
    cash_flow: {
        title: 'Cash Flow',
        tab: 'cash_financing',
        subtitle: 'Cash in, cash out and cash on hand, month by month',
        rule: 'IS rows come from the income statement; BS rows are Cash & Financing entries that also change the balance sheet. '
            + 'Cash activity is totaled across the year; prior-period cash and cash on hand are balances, so the year column shows the opening / year-end balance.',
    },
    balance_sheet: {
        title: 'Balance Sheet',
        subtitle: 'Projected month-end balances: what the business owns, owes and the owners\' equity',
        rule: 'The forecast starts from zero plus any starting cash (shown as Opening Equity). Cash comes from the cash flow, fixed assets from capital expenditure (less the book value of assets sold), '
            + 'intangible assets from intangible purchases, loans from Cash & Financing (borrowed less principal repaid) and retained earnings from net income less dividends. '
            + 'Total assets always equal total liabilities plus equity.',
    },
};

/**
 * Cash Flow (client Cash In / Cash Out layout) and Balance Sheet of a forecast.
 * View: Five-Year (one column per year) or one year's 12 months.
 */
const ForecastStatements: React.FC<{ kind: StatementKind }> = ({ kind }) => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const navigate = useNavigate();
    const { forecastId } = useParams();
    const id = Number(forecastId);
    const { getResults } = useForecastApi();

    const [results, setResults] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [view, setView] = useState<number>(0);          // 0 = five-year, 1..5 = that year's months
    const [hideEmpty, setHideEmpty] = useState<boolean>(false);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getResults(id, (_m: string, resp: any) => {
            setResults(resp?.data?.data);
            setLoading(false);
        }, (msg: string) => {
            toast.error(msg);
            setLoading(false);
        });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cash Flow and Balance Sheet share one mounted component - start at Five-Year.
    useEffect(() => {
        setView(0);
    }, [kind]);

    const tr = (key: string, fallback: string, opts?: any) => String(t(key, fallback, opts));
    const meta = META[kind];

    if (loading || !results) {
        return (
            <ForecastLayout active={kind} forecastId={id}>
                <div className="fyf-empty"><Spinner animation="border" /></div>
            </ForecastLayout>
        );
    }

    const isCash = kind === 'cash_flow';
    const label = (key: string) => tr(`cash.items.${key}`, CASH_ROW_LABELS[key] || key);
    const section = (key: string, text: string): Row => ({ key: `s-${key}`, label: text, cls: 'fyf-section' });

    let rows: Row[] = [];
    if (isCash) {
        const cf = results.cash_flow;
        rows = [
            section('in', tr('cash.cashIn', 'Cash In')),
            ...cf.cash_in.map((r: any) => ({ key: `in-${r.key}`, label: label(r.key), series: r, source: r.source })),
            { key: 'tin', label: tr('cash.totalCashIn', 'Total Cash In'), series: cf.total_cash_in, cls: 'fyf-total' },
            { key: 'prior', label: tr('cash.priorPeriod', 'Cash on Hand - Prior Period'), series: cf.prior_period_cash, source: 'BS' },
            { key: 'tcash', label: tr('cash.totalCash', 'Total Cash'), series: cf.total_cash, cls: 'fyf-total' },
            section('out', tr('cash.cashOut', 'Cash Out')),
            ...cf.cash_out.map((r: any) => ({ key: `out-${r.key}`, label: label(r.key), series: r, source: r.source })),
            { key: 'tout', label: tr('cash.totalCashOut', 'Total Cash Out'), series: cf.total_cash_out, cls: 'fyf-total' },
            { key: 'onhand', label: tr('cash.cashOnHand', 'Cash on Hand'), series: cf.cash_on_hand, cls: 'fyf-total' },
            { key: 'div', label: label('dividends'), series: cf.dividends, source: 'BS' },
            { key: 'net', label: tr('cash.netCashOnHand', 'Net Cash on Hand'), series: cf.net_cash_on_hand, cls: 'fyf-total' },
            ...(cf.activities ? [
                section('activity', tr('cash.byActivity', 'Cash by Activity')),
                { key: 'a-op', label: tr('cash.netOperating', 'Net Operating Cash (receipts - operating payments)'),
                  series: cf.activities.operating },
                { key: 'a-inv', label: tr('cash.netInvesting', 'Net Investing Cash (asset purchases / sales)'),
                  series: cf.activities.investing },
                { key: 'a-fund', label: tr('cash.netFunding', 'Net Funding Cash (loans, equity, repayments)'),
                  series: cf.activities.funding },
                { key: 'a-div', label: label('dividends'), series: cf.activities.dividends },
                { key: 'a-net', label: tr('cash.netChange', 'Net Change in Cash'), series: cf.activities.net_change,
                  cls: 'fyf-total' },
            ] : []),
        ];
    } else {
        const bs = results.balance_sheet;
        const r = (key: string, fallback: string, cls?: string): Row =>
            ({ key, label: tr(`bs.${key}`, fallback), series: bs[key], cls });
        const hasOpening = (bs.opening_equity?.monthly || []).some((v: number) => v !== 0);
        rows = [
            section('assets', tr('bs.assets', 'Assets')),
            r('cash', 'Cash'),
            r('fixed_assets', 'Fixed Assets (cost)'),
            r('accumulated_depreciation', 'Less: Accumulated Depreciation'),
            r('asset_disposals', 'Less: Book Value of Assets Sold'),
            r('intangible_assets', 'Intangible Assets (cost)'),
            r('accumulated_amortization', 'Less: Accumulated Amortization'),
            r('other_assets', 'Other Assets'),
            r('total_assets', 'Total Assets', 'fyf-total'),
            section('liabilities', tr('bs.liabilities', 'Liabilities')),
            r('short_term_loan', 'Loan (Short Term)'),
            r('long_term_loan', 'Loan / Note (Long Term)'),
            r('line_of_credit', 'Line of Credit'),
            r('lease_obligations', 'Lease Obligations'),
            r('other_liabilities', 'Other Liabilities'),
            r('total_liabilities', 'Total Liabilities', 'fyf-total'),
            section('equity', tr('bs.equity', 'Equity')),
            ...(hasOpening ? [r('opening_equity', 'Opening Equity (starting cash)')] : []),
            r('paid_in_capital', 'Paid-In Capital'),
            r('equity_partner', 'Equity Partner'),
            r('safe', 'SAFE'),
            r('retained_earnings', 'Retained Earnings'),
            r('total_equity', 'Total Equity', 'fyf-total'),
            r('total_liabilities_equity', 'Total Liabilities & Equity', 'fyf-total'),
        ];
    }

    const lo = (view - 1) * 12;
    /** Calendar year a forecast year starts in, e.g. 2027 for a Year 1 starting Oct 2027. */
    const yearLabel = (n: number): string =>
        String(Number(results.forecast.start_year) + Math.floor((Number(results.forecast.start_month) - 1 + (n - 1) * 12) / 12));
    const columns: string[] = view === 0
        ? [1, 2, 3, 4, 5].map(yearLabel)
        : results.months.slice(lo, lo + 12).map((m: any) => m.label.split(' ')[0]);
    // Cash flow adds a total column; balances need none.
    const totalHeader = isCash ? tr('results.totalCol', 'Total') : null;
    const values = (s: Series): number[] => {
        const cols = view === 0 ? s.yearly : s.monthly.slice(lo, lo + 12);
        if (!isCash) return cols;
        return [...cols, view === 0 ? s.total : s.yearly[view - 1]];
    };
    const isEmpty = (row: Row) => !!row.series && !row.cls && row.series.monthly.every((v) => v === 0);
    const shown = rows.filter((row) => !(hideEmpty && isEmpty(row)));
    const colCount = columns.length + (totalHeader ? 1 : 0) + (isCash ? 2 : 1);

    const check: number[] = isCash ? [] : results.balance_sheet.balance_check.monthly;
    const balanced = check.every((v) => Math.abs(v) < 0.01);
    const f = results.forecast;

    return (
        <ForecastLayout active={kind} forecastId={id}>
            <div className="d-flex justify-content-between align-items-start gap-3">
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <button type="button" className="fyf-back" onClick={() => navigate(URLS.FIVE_YEAR_FORECAST)}>
                        ← {t('entry.back', 'All forecasts')}
                    </button>
                    <h1>{tr(`nav.${kind}`, meta.title)}</h1>
                    <div className="fyf-sub mb-1">{tr(`statement.${kind}.subtitle`, meta.subtitle)}</div>
                    <div className="fyf-meta">
                        <strong className="text-dark text-break">{f.name}</strong>
                        {' · '}{f.forecast_type === 'new_location' ? tr('type.new_location', 'New location') : tr('type.new_business', 'New business')}
                        {' · '}{t('entry.starts', 'Starts {{month}} {{year}}', { month: MONTH_FULL_NAMES[f.start_month - 1], year: f.start_year })}
                    </div>
                </div>
                <div className="fyf-actions mt-0 flex-shrink-0 justify-content-end">
                    <ExportPdfMenu forecastId={id} forecastName={f.name} year={view || 1}
                        disabled={(results.lines || []).length === 0} />
                    <button type="button" className="fyf-secondary" onClick={() => setHideEmpty((v) => !v)}>
                        {hideEmpty ? tr('statement.showAll', 'Show all rows') : tr('statement.hideEmpty', 'Hide empty rows')}
                    </button>
                    <button type="button" className="fyf-primary"
                        onClick={() => {
                            const base = URLS.FIVE_YEAR_FORECAST_ENTRY.replace(':forecastId', String(id));
                            navigate(meta.tab ? `${base}?tab=${meta.tab}` : base);
                        }}>
                        {t('results.editEntries', 'Edit Entries')}
                    </button>
                </div>
            </div>

            <div className="fyf-yearsel">
                <button type="button" className={view === 0 ? 'on' : ''} onClick={() => setView(0)}>
                    {tr('statement.fiveYear', 'Five-Year')}
                </button>
                {[1, 2, 3, 4, 5].map((y) => (
                    <button key={y} type="button" className={y === view ? 'on' : ''} onClick={() => setView(y)}>
                        {yearLabel(y)}
                    </button>
                ))}
            </div>

            {(results.warnings || []).length > 0 && (
                <div className="fyf-note mb-3">{results.warnings.join(' ')}</div>
            )}

            <div className="fyf-panel">
                <div className="fyf-table-wrap fyf-results">
                    <table>
                        <thead>
                            <tr>
                                <th>{tr(`nav.${kind}`, meta.title)}</th>
                                {isCash && <th>{tr('cash.source', 'Source')}</th>}
                                {columns.map((c, i) => <th key={`${c}-${i}`} className="num">{c}</th>)}
                                {totalHeader && <th className="num">{totalHeader}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((r) => (r.cls === 'fyf-section' ? (
                                <tr key={r.key} className="fyf-section"><td colSpan={colCount}>{r.label}</td></tr>
                            ) : (
                                <tr key={r.key} className={r.cls || ''}>
                                    <td>{r.label}</td>
                                    {isCash && <td className="text-muted small">{r.source || ''}</td>}
                                    {values(r.series as Series).map((v, i) => (
                                        <td key={i} className={`num${v < 0 ? ' neg' : ''}`}>{money(v)}</td>
                                    ))}
                                </tr>
                            )))}
                            {!isCash && (
                                <tr className="fyf-sub-row">
                                    <td>{tr('bs.balanceCheck', 'Balance check (assets - liabilities - equity)')}</td>
                                    {values(results.balance_sheet.balance_check).map((v, i) => (
                                        <td key={i} className="num">{Math.abs(v) < 0.01 ? '✓' : money(v)}</td>
                                    ))}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {!isCash && !balanced && (
                    <div className="fyf-note text-danger">
                        {tr('bs.outOfBalance', 'The balance sheet is out of balance. Please report this forecast to support.')}
                    </div>
                )}
                <div className="fyf-note">
                    <b>{tr('detail.ruleTitle', 'Reporting rule')}</b><br />
                    {tr(`statement.${kind}.rule`, meta.rule)}
                </div>
            </div>
        </ForecastLayout>
    );
};

export default ForecastStatements;
